// src/types/service.ts
import type { Database } from '@/integrations/supabase/types';

export type ServiceOrderStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'in_progress'
  | 'completed'
  | 'billed'
  | 'cancelled';

// Catálogo de serviços (tabela services)
export interface ServiceCatalogItem {
  id: string;
  company_id: string;
  service_code?: string;
  service_name: string;
  service_type?: string;
  description?: string;
  cost_price?: number;
  sale_price?: number;
  unit?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// Equipamento cadastrado do cliente
export interface CustomerEquipment {
  id: string;
  company_id: string;
  customer_id: string;
  name: string;
  brand?: string;
  model?: string;
  serial_number?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Item de equipamento dentro de uma OS (input para criação/edição)
export interface ServiceOrderItemInput {
  item_type: 'part' | 'service';
  product_id?: string;
  service_id?: string;
  description?: string;
  quantity: number;
  unit_price: number;
  position?: number;
}

// Equipamento dentro de uma OS (input para criação/edição)
export interface ServiceOrderEquipmentInput {
  customer_equipment_id?: string;
  equipment_name: string;
  brand?: string;
  model?: string;
  serial_number?: string;
  reported_issue?: string;
  diagnosis?: string;
  accessories?: string;
  warranty_status?: 'in_warranty' | 'out_of_warranty';
  save_to_customer?: boolean;
  position?: number;
  items: ServiceOrderItemInput[];
}

// ─── Aliases derivados do schema Supabase ─────────────────────────────────────

/** Coluna extra_attributes: mapa chave→valor primitivo */
export type ExtraAttributes = Record<string, string | number | boolean | null>;

/** Row da tabela service_order_items (schema canônico) */
export type ServiceOrderItemDBRow =
  Database['public']['Tables']['service_order_items']['Row'];

/** Row da tabela service_order_equipment (schema canônico) */
export type ServiceOrderEquipmentDBRow =
  Database['public']['Tables']['service_order_equipment']['Row'];

/** Row da tabela service_orders (schema canônico) */
export type ServiceOrderDBRow =
  Database['public']['Tables']['service_orders']['Row'];

/** Row da tabela service_module_config (schema canônico) */
export type ServiceModuleConfigDBRow =
  Database['public']['Tables']['service_module_config']['Row'];

/**
 * Tipo completo retornado pelo hook useServiceOrder.
 * Inclui status mapeado para o union type da aplicação,
 * join com customers, equipamentos e fotos.
 */
export interface ServiceOrderDetail {
  // ── service_orders.Row ──────────────────────────────────────────
  id: string;
  company_id: string;
  service_order_number: string;
  /** Status cast para o union type da aplicação (inclui 'billed') */
  status: ServiceOrderStatus;
  customer_id: string;
  responsible_name: string | null;
  collected_by: string | null;
  notes: string | null;
  internal_notes: string | null;
  start_date: string;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  approved_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  sale_id: string | null;
  total_parts: number;
  total_services: number;
  total_value: number;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  // legacy columns still present in the table
  equipment_brand: string | null;
  equipment_model: string | null;
  equipment_received: string | null;
  equipment_serial: string | null;
  problem_description: string | null;
  quotation_id: string | null;
  reception_notes: string | null;
  service_description: string | null;
  nfse_id: string | null;
  nfse_status: string | null;
  contract_id: string | null;
  // ── Assinatura na coleta (espelha o padrão de sales/loans) ────────
  signature_data: string | null;
  signature_customer_name: string | null;
  signature_customer_document: string | null;
  signature_date: string | null;
  signature_ip: string | null;
  // ── Joins ────────────────────────────────────────────────────────
  /** Dados do cliente — via join customers!service_orders_customer_id_fkey */
  customers: {
    id: string;
    name: string;
    document_number: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    address_number: string | null;
    neighborhood: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
  } | null;
  service_order_equipment: Array<{
    id: string;
    customer_equipment_id: string | null;
    equipment_name: string;
    brand: string | null;
    model: string | null;
    serial_number: string | null;
    reported_issue: string | null;
    diagnosis: string | null;
    accessories: string | null;
    warranty_status: string | null;
    position: number;
    extra_attributes: ExtraAttributes;
    service_order_items: Array<{
      id: string;
      item_type: string;
      product_id: string | null;
      service_id: string | null;
      description: string | null;
      quantity: number;
      unit_price: number;
      total_price: number | null;
      position: number;
    }>;
  }>;
  service_order_photos: Array<{
    id: string;
    storage_path: string;
    caption: string | null;
    position: number;
    uploaded_at: string | null;
  }>;
}

// OS resumida (retorno da RPC get_service_orders_filtered)
export interface ServiceOrderSummary {
  id: string;
  service_order_number: string;
  status: ServiceOrderStatus;
  customer_id: string;
  customer_name: string;
  responsible_name?: string;
  total_value: number;
  total_parts: number;
  total_services: number;
  equipment_count: number;
  created_at: string;
  approved_at?: string;
  completed_at?: string;
  sale_id?: string;
  total_count: number;
}

// Labels de status para exibição
export const SERVICE_ORDER_STATUS_LABELS: Record<ServiceOrderStatus, string> = {
  draft:            'Coleta',
  pending_approval: 'Aguardando Aprovação',
  approved:         'Aprovado',
  in_progress:      'Em Produção',
  completed:        'Concluído',
  billed:           'Finalizado',
  cancelled:        'Cancelado',
};

// Cores de badge por status (Tailwind)
export const SERVICE_ORDER_STATUS_COLORS: Record<ServiceOrderStatus, string> = {
  draft:            'bg-gray-100 text-gray-600',
  pending_approval: 'bg-yellow-100 text-yellow-700',
  approved:         'bg-blue-100 text-blue-700',
  in_progress:      'bg-purple-100 text-purple-700',
  completed:        'bg-green-100 text-green-700',
  billed:           'bg-emerald-100 text-emerald-800 border-emerald-200',
  cancelled:        'bg-red-100 text-red-600',
};

// ─── Tipos legados (mantidos para compat. com Sprint 1–2, remover no Sprint 4) ─

/** @deprecated Use ServiceCatalogItem */
export interface Service {
  id: string;
  company_id: string;
  service_code?: string;
  service_name: string;
  service_type: 'maintenance' | 'repair' | 'installation' | 'calibration' | 'inspection' | 'other';
  description?: string;
  cost_price?: number;
  sale_price?: number;
  estimated_duration_hours?: number;
  active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/** @deprecated Use ServiceOrderDBRow */
export interface ServiceOrder {
  id: string;
  company_id: string;
  service_order_number: string;
  customer_id: string;
  status: 'budget_pending' | 'service_pending' | 'in_progress' | 'completed' | 'delivered' | 'cancelled';
  start_date: string;
  expected_delivery_date?: string;
  actual_delivery_date?: string;
  responsible_user_id?: string;
  equipment_received: string;
  equipment_serial?: string;
  equipment_brand?: string;
  equipment_model?: string;
  reception_notes?: string;
  problem_description?: string;
  service_description?: string;
  internal_notes?: string;
  quotation_id?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  deleted_by?: string;
}

/** @deprecated Use ServiceOrderSummary */
export interface ServiceOrderWithCustomer extends ServiceOrder {
  customer?: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
  };
  responsible?: {
    user_id: string;
    full_name: string;
  };
}

/** @deprecated */
export interface ServiceOrderWithDetails extends ServiceOrder {
  customer: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    state?: string;
    document_number?: string;
  };
  responsible?: {
    id: string;
    full_name: string;
  };
  photos: ServiceOrderPhoto[];
  quotation?: {
    id: string;
    quotation_number: string;
    total_value: number;
    status: string;
  };
}

/** @deprecated */
export interface ServiceOrderFilters {
  startDate?: Date;
  endDate?: Date;
  customerId?: string;
  status?: string;
  searchTerm?: string;
}

// ─── service_module_config ────────────────────────────────────────────────────
export interface ServiceModuleConfig {
  id: string;
  company_id: string;
  equipment_label: string;
  equipment_plural: string;
  order_label: string;
  order_plural: string;
  order_prefix: string;
  show_extra_attributes: boolean;
  extra_attribute_labels: Array<{
    key: string;
    label: string;
    type: 'text' | 'number' | 'date';
  }>;
  created_at: string;
  updated_at: string;
}

// ─── Upload de fotos ──────────────────────────────────────────────────────────
export interface ServiceOrderPhoto {
  id: string;
  service_order_id: string;
  storage_path: string;
  caption: string | null;
  position: number;
  created_at: string;
  publicUrl?: string;
}

// ─── Status transitions ───────────────────────────────────────────────────────
export type ServiceOrderStatusTransition = {
  from: ServiceOrderStatus;
  to: ServiceOrderStatus;
  label: string;
  requiresPermission: 'services:create' | 'services:approve' | 'services:convert';
  confirmMessage: string;
};

export const SERVICE_ORDER_TRANSITIONS: ServiceOrderStatusTransition[] = [
  { from: 'pending_approval', to: 'in_progress', label: 'Iniciar Produção',
    requiresPermission: 'services:approve', confirmMessage: 'Mover esta OS para produção?' },
  { from: 'pending_approval', to: 'draft', label: 'Retornar para Coleta',
    requiresPermission: 'services:approve', confirmMessage: 'Retornar esta OS para a fase de coleta?' },
  { from: 'draft', to: 'cancelled', label: 'Cancelar OS',
    requiresPermission: 'services:create', confirmMessage: 'Cancelar esta Ordem de Serviço? Esta ação não pode ser desfeita.' },
  { from: 'pending_approval', to: 'cancelled', label: 'Cancelar OS',
    requiresPermission: 'services:approve', confirmMessage: 'Cancelar esta Ordem de Serviço? Esta ação não pode ser desfeita.' },
  { from: 'in_progress', to: 'cancelled', label: 'Cancelar OS',
    requiresPermission: 'services:approve', confirmMessage: 'Cancelar esta Ordem de Serviço? Esta ação não pode ser desfeita.' },
];
